import React, { ReactNode, useState } from 'react';
import moment from 'moment';
import camelCase from 'lodash/camelCase';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from 'v5/design-system/Dialog';
import { Button } from 'v5/design-system/Button';
import { EventHoursInputOptions } from 'v1/components/shared';
import {
  DURATION_TYPES,
  DURATION_DEFAULTS
} from 'v1/helpers/byModel/EventHelper';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
interface AvailabilityEvent {
  title: string;
  start: string | Date;
  end: string | Date;
  backgroundColor?: string;
  startStr?: string;
  endStr?: string;
}

interface LocalChanges {
  dateType: string;
  estimatedDailyHours: string;
  estimatedDailyMinutes: string;
  estimatedStartTime: string;
  estimatedEndTime: string;
}

interface AvailabilityEditDialogProps {
  event: AvailabilityEvent;
  publicId: string;
  dateType: string;
  estimatedDailyHours: string;
  estimatedDailyMinutes: string;
  estimatedStartTime: string;
  estimatedEndTime: string;
  onHandleChange: (
    id: string,
    durationType: string,
    updatedDuration: any
  ) => void;
  onDeleteAvailability: (id: string) => void;
  deleteLabel: string;
  renderTrigger: (props: React.HTMLAttributes<HTMLElement>) => ReactNode;
  tooltip: ReactNode;
}

const AvailabilityEditDialog: React.FC<AvailabilityEditDialogProps> = ({
  event,
  publicId,
  dateType,
  estimatedDailyHours,
  estimatedDailyMinutes,
  estimatedStartTime,
  estimatedEndTime,
  onHandleChange,
  onDeleteAvailability,
  deleteLabel,
  renderTrigger,
  tooltip
}) => {
  const [localChanges, setLocalChanges] = useState<LocalChanges>({
    dateType,
    estimatedDailyHours,
    estimatedDailyMinutes,
    estimatedStartTime,
    estimatedEndTime
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { t, i18n } = useTranslation('resource-portal');

  const getDateRange = (): string => {
    // Set moment locale based on i18n language (matching the locale mapping from i18n.moment.ts)
    const locale = i18n.language === 'zh' ? 'zh-cn' : i18n.language;
    moment.locale(locale);

    const startDate = moment(event.start).format('ddd, MMM D');
    const endDate = moment(event.end).subtract(1, 'day').format('ddd, MMM D'); // Subtract 1 day because FullCalendar end is exclusive

    if (startDate === endDate) {
      return startDate;
    }
    return `${startDate} - ${endDate}`;
  };

  const handleLocalChange = (field: string, value: string): void => {
    if (field === 'date_type') {
      const newDurationType = value;
      let newChanges = { ...localChanges, dateType: newDurationType };

      if (newDurationType === DURATION_TYPES.RECURRING_SPECIFIC_HOURS) {
        newChanges = {
          ...newChanges,
          estimatedStartTime:
            localChanges.estimatedStartTime ||
            DURATION_DEFAULTS.ESTIMATED_START_TIME.toString(),
          estimatedEndTime:
            localChanges.estimatedEndTime ||
            DURATION_DEFAULTS.ESTIMATED_END_TIME.toString()
        };
      } else if (newDurationType === DURATION_TYPES.RECURRING_HOURS) {
        newChanges = {
          ...newChanges,
          estimatedDailyHours:
            localChanges.estimatedDailyHours ||
            DURATION_DEFAULTS.ESTIMATED_DAILY_HOURS.toString(),
          estimatedDailyMinutes:
            localChanges.estimatedDailyMinutes ||
            DURATION_DEFAULTS.ESTIMATED_DAILY_MINUTES.toString()
        };
      }

      setLocalChanges(newChanges);
    } else {
      // Convert snake_case from v1 EventHoursInputOptions to camelCase (e.g., 'estimated_start_time' → 'estimatedStartTime')
      setLocalChanges(prev => ({
        ...prev,
        [camelCase(field)]: value
      }));
    }
  };

  const handleSave = (): void => {
    const { dateType: newDurationType, ...duration } = localChanges;
    onHandleChange(publicId, newDurationType, {
      dateType: newDurationType,
      ...duration
    });
    setIsOpen(false);
  };

  const handleCancel = (): void => {
    setLocalChanges({
      dateType,
      estimatedDailyHours,
      estimatedDailyMinutes,
      estimatedStartTime,
      estimatedEndTime
    });
    setIsOpen(false);
  };

  const handleDelete = (): void => {
    onDeleteAvailability(publicId);
    setIsOpen(false);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
        if (!open) {
          handleCancel();
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            {renderTrigger({
              onKeyDown: e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsOpen(true);
                }
              }
            })}
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('CalendarRoute.availabilityEditDialog.title')}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {t('CalendarRoute.availabilityEditDialog.description')}:{' '}
          <strong>{getDateRange()}</strong>
        </DialogDescription>
        <DialogBody className="[&_.RadioGroup>*]:border-l-0!">
          <EventHoursInputOptions
            handleChange={handleLocalChange}
            durationType={localChanges.dateType}
            estimatedDailyHours={localChanges.estimatedDailyHours}
            estimatedDailyMinutes={localChanges.estimatedDailyMinutes}
            estimatedStartTime={localChanges.estimatedStartTime}
            estimatedEndTime={localChanges.estimatedEndTime}
          />
        </DialogBody>
        <DialogFooter>
          <div className={'w-full flex items-center justify-between'}>
            <Button size="sm" variant="danger" onClick={handleDelete}>
              {deleteLabel}
            </Button>
            <Button size="sm" variant="primaryAlt" onClick={handleSave}>
              {t('CalendarRoute.availabilityEditDialog.saveChanges')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvailabilityEditDialog;
